/**
 * Scale Bar Utility for Map Export
 *
 * Computes a human-readable scale bar based on the current map view
 * and draws it onto a canvas. Used during map capture for report exports.
 *
 * Handles latitude correction for Web Mercator (EPSG:3857) projection
 * to ensure accurate ground distance representation.
 *
 * @module scaleBar
 */

import { getPointResolution } from 'ol/proj';

/**
 * Predefined "nice" distances for scale bar labels.
 * The algorithm picks the largest value whose pixel width
 * fits within the maximum allowed bar width.
 */
const NICE_DISTANCES = [
	1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000,
	500000,
];

const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * Format a distance in meters to a human-readable label.
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted label (e.g., "100 m", "2 km")
 */
const formatDistance = meters => {
	if (meters >= 1000) {
		const km = meters / 1000;
		return `${km % 1 === 0 ? km.toFixed(0) : km.toFixed(1)} km`;
	}
	return `${meters} m`;
};

/**
 * Calculate the scale bar parameters for a given map view.
 *
 * @param {import('ol/Map').default} map - OpenLayers map instance
 * @param {number} maxBarWidth - Maximum bar width in CSS pixels
 * @returns {{ distance: number, pixelWidth: number, label: string } | null}
 */
export const calculateScaleBar = (map, maxBarWidth = 150) => {
	if (!map) return null;

	const view = map.getView();
	if (!view) return null;

	const resolution = view.getResolution();
	const center = view.getCenter();
	const projection = view.getProjection();

	if (!resolution || !center || !projection) return null;

	// Get meters per pixel corrected for latitude distortion
	const metersPerPixel = getPointResolution(projection, resolution, center, 'm');

	if (!metersPerPixel || !isFinite(metersPerPixel) || metersPerPixel <= 0) return null;

	// Find the largest "nice" distance that fits within maxBarWidth
	let bestDistance = NICE_DISTANCES[0];
	for (const dist of NICE_DISTANCES) {
		const pixelWidth = dist / metersPerPixel;
		if (pixelWidth <= maxBarWidth) {
			bestDistance = dist;
		} else {
			break;
		}
	}

	const pixelWidth = bestDistance / metersPerPixel;

	return {
		distance: bestDistance,
		pixelWidth,
		label: formatDistance(bestDistance),
	};
};

/**
 * Draw a scale bar onto a canvas context.
 * Positioned in the bottom-left corner with a semi-transparent background.
 *
 * Assumes OpenLayers default pixelRatio (devicePixelRatio).
 *
 * @param {HTMLCanvasElement} canvas - Target canvas element
 * @param {import('ol/Map').default} map - OpenLayers map instance
 */
export const drawScaleBar = (canvas, map) => {
	if (!canvas || !map) return;

	const ctx = canvas.getContext('2d');
	if (!ctx) return;

	// OL renders canvases at devicePixelRatio. The resolution from getView()
	// is in CSS pixels, so we must scale the bar width to match the canvas.
	const dpr = (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1;

	// Scale maxBarWidth relative to canvas size (roughly 20% of width, max 150 CSS px)
	const maxBarWidth = Math.min(150 * dpr, Math.round(canvas.width * 0.2));
	const scaleInfo = calculateScaleBar(map, maxBarWidth / dpr);

	if (!scaleInfo) return;

	// pixelWidth from calculateScaleBar is in CSS pixels — convert to device pixels
	const pixelWidth = scaleInfo.pixelWidth * dpr;
	if (pixelWidth < 1) return;

	const { label } = scaleInfo;

	// Layout constants scaled for DPR
	const padding = 10 * dpr;
	const barHeight = 4 * dpr;
	const fontSize = Math.round(Math.max(11 * dpr, canvas.width * 0.012));
	const margin = 16 * dpr;
	const tickOverhang = 2 * dpr;

	// Measure text width
	const fontSpec = `bold ${fontSize}px ${FONT_FAMILY}`;
	ctx.font = fontSpec;
	const textWidth = ctx.measureText(label).width;

	// Background dimensions — use max of bar and text width to prevent text overflow when centered
	const contentWidth = Math.max(pixelWidth, textWidth);
	const bgWidth = contentWidth + padding * 2;
	const bgHeight = barHeight + fontSize + padding * 2 + 4 * dpr;

	// Position: bottom-right corner
	const x = canvas.width - margin - bgWidth;
	const y = canvas.height - margin - bgHeight;

	// Draw solid dark background
	ctx.save();
	ctx.fillStyle = '#1a1a2e';
	ctx.beginPath();
	if (ctx.roundRect) {
		ctx.roundRect(x, y, bgWidth, bgHeight, 4 * dpr);
	} else {
		ctx.rect(x, y, bgWidth, bgHeight);
	}
	ctx.fill();

	// Draw the scale bar
	const barX = x + padding;
	const barY = y + padding;

	ctx.fillStyle = '#c0c0c0';
	ctx.fillRect(barX, barY, pixelWidth, barHeight);

	// Draw bar border ticks (left and right ends)
	ctx.strokeStyle = '#c0c0c0';
	ctx.lineWidth = 1.5 * dpr;

	ctx.beginPath();
	ctx.moveTo(barX, barY - tickOverhang);
	ctx.lineTo(barX, barY + barHeight + tickOverhang);
	ctx.stroke();

	ctx.beginPath();
	ctx.moveTo(barX + pixelWidth, barY - tickOverhang);
	ctx.lineTo(barX + pixelWidth, barY + barHeight + tickOverhang);
	ctx.stroke();

	// Draw label text below the bar (centered relative to background)
	ctx.fillStyle = '#c0c0c0';
	ctx.textBaseline = 'top';
	ctx.textAlign = 'center';
	ctx.fillText(label, x + bgWidth / 2, barY + barHeight + 4 * dpr);

	ctx.restore();
};
